/**
 * /suprimentos/configuracao — ConfiguracaoSuprimentos da unidade (D33) +
 * motivos de ajuste. Toda config nasce com tela (Definição de Pronto §3).
 */
import React, { useCallback, useEffect, useState } from "react";
import Checkbox from "../../components/Checkbox";
import { FiAlertCircle, FiPlus } from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import { useAuth } from "../../context/AuthContext";
import { listarDepositos } from "../../services/estoque";
import {
  atualizarMotivoAjuste, criarMotivoAjuste, linhasDe, listarMotivosAjuste,
  obterConfiguracaoSuprimentos, salvarConfiguracaoSuprimentos,
} from "../../services/suprimentos";
import { fmtMoeda } from "./helpers";
import {
  Card, ErrorMsg, Field, FormGrid, InlineHint, Input, Pill, SecondaryButton,
  SectionTitle, Select, Table, TableWrapper, TruncCell,
} from "./styles";
import { Vazio } from "./_shared/Estados";

const MOTIVO_VAZIO = { codigo: "", nome: "", eh_perda: true, exige_aprovacao_acima_de: "" };

export default function ConfiguracaoSuprimentos() {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const podeConfigurar = isMaster || hasAllPermissions(["estoque:configurar"]);
  const [cfg, setCfg] = useState(null);
  const [depositos, setDepositos] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [novoMotivo, setNovoMotivo] = useState(null);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const [c, d, m] = await Promise.all([
        obterConfiguracaoSuprimentos(request),
        listarDepositos(request),
        listarMotivosAjuste(request),
      ]);
      setCfg(c);
      setDepositos(linhasDe(d));
      setMotivos(linhasDe(m));
      setErro(null);
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.message || "Erro ao carregar configuração.");
    }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  const set = (k) => (e) => setCfg((p) => ({ ...p, [k]: e.target.value }));

  const salvar = async () => {
    const salvo = await salvarConfiguracaoSuprimentos(request, {
      deposito_recebimento: cfg.deposito_recebimento || null,
      deposito_saida_almoxarifado: cfg.deposito_saida_almoxarifado || null,
      deposito_sobras: cfg.deposito_sobras || null,
      limite_aprovacao_ajuste: cfg.limite_aprovacao_ajuste || "0",
      refilo_padrao_mm: cfg.refilo_padrao_mm || "10",
      dias_uteis_antes_do_corte: cfg.dias_uteis_antes_do_corte === "" || cfg.dias_uteis_antes_do_corte == null ? 3 : Number(cfg.dias_uteis_antes_do_corte),
    });
    setCfg(salvo);
  };

  const salvarMotivo = async () => {
    if (!novoMotivo.codigo || !novoMotivo.nome) throw new Error("Código e nome são obrigatórios.");
    await criarMotivoAjuste(request, {
      ...novoMotivo,
      exige_aprovacao_acima_de: novoMotivo.exige_aprovacao_acima_de || null,
    });
    setNovoMotivo(null);
    await carregar();
  };

  const opcoesDeposito = depositos.filter((d) => d.ativo);

  return (
    <>
      <EntityHeader
        title="Configuração de Suprimentos"
        subtitle="Depósitos padrão, limite de aprovação de ajuste, refilo e motivos de ajuste."
      />
      {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}

      {cfg && (
        <Card>
          <SectionTitle>Padrões da unidade</SectionTitle>
          <FormGrid>
            <Field>
              Depósito de recebimento (entrada de OC)
              <Select value={cfg.deposito_recebimento || ""} onChange={set("deposito_recebimento")} disabled={!podeConfigurar}>
                <option value="">— Depósito principal —</option>
                {opcoesDeposito.map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}
              </Select>
            </Field>
            <Field>
              Depósito de saída do almoxarifado (RQA/RQI)
              <Select value={cfg.deposito_saida_almoxarifado || ""} onChange={set("deposito_saida_almoxarifado")} disabled={!podeConfigurar}>
                <option value="">— Depósito principal —</option>
                {opcoesDeposito.map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}
              </Select>
            </Field>
            <Field>
              Depósito de sobras
              <Select value={cfg.deposito_sobras || ""} onChange={set("deposito_sobras")} disabled={!podeConfigurar}>
                <option value="">— Depósito principal —</option>
                {opcoesDeposito.map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}
              </Select>
            </Field>
            <Field>
              Limite de aprovação de ajuste (R$)
              <Input type="number" step="0.01" min="0" value={cfg.limite_aprovacao_ajuste ?? ""} onChange={set("limite_aprovacao_ajuste")} disabled={!podeConfigurar} />
              <InlineHint>Ajuste manual acima deste valor (qtd × custo médio) espera aprovação. Zero = tudo aprova na hora.</InlineHint>
            </Field>
            <Field>
              Refilo padrão da chapa (mm por lado)
              <Input type="number" step="0.5" min="0" value={cfg.refilo_padrao_mm ?? ""} onChange={set("refilo_padrao_mm")} disabled={!podeConfigurar} />
              <InlineHint>Usado quando o produto não informa o próprio refilo. Padrão do sistema: 10 mm.</InlineHint>
            </Field>
            <Field>
              Dias úteis antes do corte (requisições do PharuCut)
              <Input type="number" step="1" min="0" max="30" value={cfg.dias_uteis_antes_do_corte ?? ""} onChange={set("dias_uteis_antes_do_corte")} disabled={!podeConfigurar} />
              <InlineHint>O prazo de cotação é a data em que a serra precisa do material menos estes dias úteis. Essa data vem do PharuCut (hoje + prazo de compras, em Terminal → Configuração) e pode ser alterada na própria requisição. Padrão: 3.</InlineHint>
            </Field>
          </FormGrid>
          {podeConfigurar && (
            <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
              <AsyncButton successMessage="Configuração salva." onClick={salvar}>Salvar</AsyncButton>
            </div>
          )}
        </Card>
      )}

      <Card>
        <SectionTitle>Motivos de ajuste</SectionTitle>
        <InlineHint>Todo ajuste manual de saldo exige um motivo. Motivo pode ter limite próprio de aprovação.</InlineHint>
        {podeConfigurar && !novoMotivo && (
          <div style={{ margin: "8px 0" }}>
            <SecondaryButton type="button" onClick={() => setNovoMotivo({ ...MOTIVO_VAZIO })}><FiPlus size={12} /> Novo motivo</SecondaryButton>
          </div>
        )}
        {novoMotivo && (
          <FormGrid style={{ margin: "8px 0", alignItems: "end" }}>
            <Field>Código<Input value={novoMotivo.codigo} maxLength={20} onChange={(e) => setNovoMotivo((p) => ({ ...p, codigo: e.target.value.toUpperCase() }))} placeholder="QUEBRA" /></Field>
            <Field>Nome<Input value={novoMotivo.nome} maxLength={80} onChange={(e) => setNovoMotivo((p) => ({ ...p, nome: e.target.value }))} placeholder="Quebra / dano" /></Field>
            <Field>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Checkbox checked={novoMotivo.eh_perda} onChange={(e) => setNovoMotivo((p) => ({ ...p, eh_perda: e.target.checked }))} /> É perda
              </label>
            </Field>
            <Field>Exige aprovação acima de (R$)<Input type="number" step="0.01" min="0" value={novoMotivo.exige_aprovacao_acima_de} onChange={(e) => setNovoMotivo((p) => ({ ...p, exige_aprovacao_acima_de: e.target.value }))} /></Field>
            <div style={{ display: "flex", gap: 6 }}>
              <AsyncButton successMessage="Motivo criado." onClick={salvarMotivo}>Salvar</AsyncButton>
              <SecondaryButton type="button" onClick={() => setNovoMotivo(null)}>Cancelar</SecondaryButton>
            </div>
          </FormGrid>
        )}
        {motivos.length === 0 ? <Vazio titulo="Nenhum motivo cadastrado" /> : (
          <TableWrapper>
            <Table>
              <colgroup><col style={{ width: "16%" }} /><col style={{ width: "36%" }} /><col style={{ width: "12%" }} /><col style={{ width: "20%" }} /><col style={{ width: "16%" }} /></colgroup>
              <thead><tr><th>Código</th><th>Nome</th><th>Perda</th><th>Aprovação acima de</th><th>Situação</th></tr></thead>
              <tbody>
                {motivos.map((m) => (
                  <tr key={m.id}>
                    <td><TruncCell>{m.codigo}</TruncCell></td>
                    <td title={m.nome}><TruncCell>{m.nome}</TruncCell></td>
                    <td>{m.eh_perda ? "Sim" : "Não"}</td>
                    <td>{m.exige_aprovacao_acima_de ? fmtMoeda(m.exige_aprovacao_acima_de) : "limite da unidade"}</td>
                    <td>
                      <Pill $tone={m.ativo ? "success" : "neutral"}>{m.ativo ? "Ativo" : "Inativo"}</Pill>
                      {podeConfigurar && (
                        <SecondaryButton type="button" style={{ marginLeft: 6 }} onClick={async () => { await atualizarMotivoAjuste(request, m.id, { ativo: !m.ativo }); await carregar(); }}>
                          {m.ativo ? "Desativar" : "Reativar"}
                        </SecondaryButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>
    </>
  );
}
